import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatDivider } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSortModule, SortDirection } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { TransactionsListFilter } from '../../../../models/transactions-list-filter.model';
import { IPayrollSettingsList } from '../../../../pages/dashboard/models/payroll-settings-list.interface';
import { PayrollSettingsService } from '../../../../pages/dashboard/services/payroll-settings.service';
import { TransactionListService } from '../../../../pages/dashboard/services/transaction-list.service';
import { SchedulePaymentV2Component } from '../../../../pages/dashboardV2/components/schedule-paymentV2/schedule-paymentV2.component';
import { PdfViewerService } from '../../../../services/pdf-viewer.service';
import { ThemesService } from '../../../../services/themes.service';
import { DealStatusEnum } from '../../../advance-application/models/deal-status.enum';
import { DealTransactionStatusEnum } from '../../../advance-application/models/deal-transaction-status.enum';
import { IExternalDealDetailsDTO } from '../../../advance-application/models/external-deal.interface';
import { PromisedPaymentStatusEnum } from '../../../advance-application/models/promised-payment-status.enum';
import { TransactionStatusEnum } from '../../../advance-application/models/transaction-status.enum';
import { DealService } from '../../../advance-application/services/deal.service';
import { PromisedPaymentService } from '../../../advance-application/services/promised-payment.service';
import { ProductEnum } from '../../../authentication/models/product.enum';
import { User } from '../../../authentication/models/v1-api.interface';
import { AuthenticationService } from '../../../authentication/services/authentication.service';
import { BankInfoPipe } from '../../../pipes/bank-info.pipe';
import { ConvertToDealTransactionStatusPipe } from '../../../pipes/convertToDealTransactionStatus.pipe';
import { CurrencyJsPipe } from '../../../pipes/currency-js.pipe';
import { DatePipe } from '../../../pipes/date.pipe';
import { LocalisePipe } from '../../../pipes/localise.pipe';
import { DealStatusWithColorComponent } from '../../../shared/components/deal-status-with-color/deal-status-with-color.component';
import { PreviewDocumentComponent } from '../../../shared/components/preview-document/preview-document.component';
import { IAgentCommissionTransaction } from '../../../shared/models/agent-commission-transaction.interface';
import { DownloadEnum } from '../../../shared/models/download.enum';
import { FormatDateEnum } from '../../../shared/models/format-date.enum';
import { List } from '../../../shared/models/list.model';
import { ITransactionList } from '../../../shared/models/transaction.interface';
import { TransactionPayoutStateToName } from '../../../shared/models/transaction-payout-state.enum';
import {
  TransactionTypeEnum,
  TransactionTypeNames,
} from '../../../shared/models/transaction-type.enum';
import { ITransactionWithAuthorizationList } from '../../../shared/models/transaction-with-authorization.interface';
import { FileNamePipe } from '../../../shared/pipes/file-name.pipe';
import { FullAddressPipe } from '../../../shared/pipes/full-address.pipe';
import { SharedService } from '../../../shared/services/shared.service';
import { TransactionService } from '../../../shared/services/transaction.service';
import { ConfirmDepositDistributionsComponent } from '../confirm-deposit-distributions/confirm-deposit-distributions.component';

@Component({
  selector: 'app-commission-details-new',
  templateUrl: './commission-details-new.component.html',
  styleUrls: ['./commission-details-new.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatButtonModule,
    MatTableModule,
    MatSortModule,
    MatExpansionModule,
    MatDialogModule,
    LocalisePipe,
    CurrencyJsPipe,
    DatePipe,
    FullAddressPipe,
    BankInfoPipe,
    PreviewDocumentComponent,
    FileNamePipe,
    DealStatusWithColorComponent,
    MatDivider,
    ConvertToDealTransactionStatusPipe,
  ],
})
export class CommissionDetailsNewComponent implements OnInit, OnDestroy {
  constructor(
    private activatedRoute: ActivatedRoute,
    private dealService: DealService,
    private authenticationService: AuthenticationService,
    private promisedPaymentService: PromisedPaymentService,
    private toastrService: ToastrService,
    private payrollSettingsService: PayrollSettingsService,
    private sharedService: SharedService,
    private themesService: ThemesService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private pdfViewerService: PdfViewerService,
    private transactionListService: TransactionListService,
    private transactionService: TransactionService,
  ) {}
  @Input() queryParams: any;

  protected readonly TransactionTypeEnum = TransactionTypeEnum;
  public dealId: number;
  public loading = true;
  private subscriptions: Subscription[] = [];
  public paymentDueStatuses = [
    DealStatusEnum.GracePeriod,
    DealStatusEnum.Deferred,
    DealStatusEnum.Late,
    DealStatusEnum.Defaulted,
  ];
  public formatDateEnum = FormatDateEnum;
  public dealStatusEnum = DealStatusEnum;
  public readonly dealTransactionStatusEnum = DealTransactionStatusEnum;
  public readonly TransactionStatusEnum = TransactionStatusEnum;
  public notConfirmedDealsTotal = 0;
  public externalDeal: IExternalDealDetailsDTO;
  public limit = 30;
  public page = 1;
  public sortBy = 'depositAmount';
  public sortDirection?: SortDirection;
  public user: User = this.authenticationService.getProfileState();
  public readonly productEnum = ProductEnum;
  public loadingFile = false;
  public error = '';
  public showDetails = false;
  public showTransactions = false;
  public transactionList: List<ITransactionWithAuthorizationList>;
  public readonly PromisedPaymentStatusEnum = PromisedPaymentStatusEnum;
  public transactionPayoutStateNames: Record<string, string> =
    TransactionPayoutStateToName;
  public transactionTypeNames: Record<string, string> = TransactionTypeNames;
  public agentCommissionTransactions: IAgentCommissionTransaction[] = [];
  public payrollSettingsList: IPayrollSettingsList[] = [];
  public options = new TransactionsListFilter();

  public columnsPayroll = [
    'accountNickname',
    'paymentDate',
    'account',
    'distributionAmount',
    'depositAmount',
    'depositStatus',
  ];

  public subColumns = [
    'date',
    'payoutState',
    'amount',
    'unpaidAmount',
    'type',
    'creditCardNumber',
    'description',
  ];
  public columns = [...this.subColumns, 'actions'];
  public downloadEnum = DownloadEnum;
  public isExpansionDetailRow = (_: number, row: any): boolean => {
    return !!row.expanded;
  };

  async ngOnInit(): Promise<void> {
    if (this.queryParams) {
      this.byQueryParams(this.queryParams);
    } else {
      this.subscriptions.push(
        this.activatedRoute.params.subscribe((params) =>
          this.byQueryParams(params as any),
        ),
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  public byQueryParams(queryParams: { id: string }): void {
    this.dealId = parseInt(queryParams.id, 10);
    this.load(this.dealId);
  }

  private async load(dealId: number): Promise<void> {
    this.loading = true;

    try {
      this.externalDeal = await firstValueFrom(
        this.dealService.getExternalDeal(dealId),
      );

      if (this.externalDeal?.payrollToClocDealLinkId) {
        this.options.dealIds = [this.externalDeal.payrollToClocDealLinkId];
        this.options.withPromisedPaymentsFailed = true;
        this.transactionList =
          await this.transactionListService.getTransactionListByOptions(
            this.options,
          );

        this.transactionList.list = this.transactionList.list.filter(
          (transaction) => !transaction.resolvePromisedPaymentId,
        );
      }

      this.agentCommissionTransactions = [
        ...this.externalDeal?.agentCommissionTransactions,
      ].sort((trx1, trx2) => {
        return this.sortDirection === 'asc'
          ? trx1?.amount.value - trx2?.amount?.value
          : trx2?.amount?.value - trx1?.amount?.value;
      });
      this.payrollSettingsList = await firstValueFrom(
        this.payrollSettingsService.get(),
      );
    } catch (e) {
      this.toastrService.error(this.sharedService.getErrorText(e, true));
    }

    this.loading = false;
  }

  public setSort(sortBy?: string, sortDirection?: SortDirection): void {
    this.sortBy = sortBy || 'depositAmount';
    this.sortDirection = sortDirection;
    this.load(this.dealId);
  }

  public confirmDepositDistributions(externalDealId: number): void {
    const ref = this.dialog.open(ConfirmDepositDistributionsComponent, {
      ...ConfirmDepositDistributionsComponent.DIALOG_CONFIG,
      panelClass: [
        'dialog-without-padding',
        this.themesService.getDarkThemeClass(),
      ],
      backdropClass: this.themesService.getDarkThemeClass(),
    });
    ref.componentInstance.externalDealId = externalDealId;
    ref
      .afterClosed()
      .pipe(filter((success) => !!success))
      .subscribe(() => {
        this.load(this.dealId);
      });
  }

  public async clickToggleSubTransactions(
    row: ITransactionList,
  ): Promise<void> {
    const willExpand = !row.expanded;
    row.expanded = willExpand;

    this.transactionList.list = [...this.transactionList.list];
    this.cdr.markForCheck();

    if (!willExpand) {
      return;
    }
    if (row.loading) {
      return;
    }
    if (row.subTransactions?.length) {
      return;
    }

    row.loading = true;
    this.cdr.markForCheck();

    try {
      const result = await firstValueFrom(
        this.transactionService.get({
          sortDirection: 'asc',
          sortBy: '',
          offset: 0,
          limit: 100,
          groupId: row.groupId,
        }),
      );

      row.subTransactions = result.list;
    } catch (e) {
      this.toastrService.error(this.sharedService.getErrorText(e, true));
    } finally {
      row.loading = false;
      this.transactionList.list = [...this.transactionList.list];
      this.cdr.markForCheck();
    }
  }

  public cancelPayment(id: number, confirmTmp: TemplateRef<any>): void {
    const dialogRef = this.dialog.open(confirmTmp, {
      width: '50vw',
      minWidth: '500px',
      panelClass: [
        this.themesService.getDarkThemeClass(),
        'bank-settings-modal',
      ],
      backdropClass: this.themesService.getDarkThemeClass(),
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (!result) {
        return;
      }
      await this.promisedPaymentService.cancel(id).toPromise();
      this.load(this.dealId);
    });
  }

  public downloadExternalDealStatement(
    dealId: number,
    preview?: boolean,
  ): void {
    const url = `/api/external-deal/${dealId}/statement?download=${
      preview ? DownloadEnum.Inline : DownloadEnum.Attachment
    }`;
    if (preview) {
      this.pdfViewerService.preview(url, true);
      return;
    }
    window.open(url, '_blank');
  }

  public schedulePayment(failedPromisedPaymentId: number): void {
    const dialogRef = this.dialog.open(SchedulePaymentV2Component, {
      panelClass: this.themesService.getDarkThemeClass(),
      backdropClass: this.themesService.getDarkThemeClass(),
    });
    dialogRef.componentInstance.dealId = this.dealId;
    dialogRef.componentInstance.failedPromisedPaymentId =
      failedPromisedPaymentId;
    dialogRef
      .afterClosed()
      .pipe(filter((res) => res))
      .subscribe(async () => {
        if (failedPromisedPaymentId) {
          await this.promisedPaymentService
            .hideFailedPromisedPayment(failedPromisedPaymentId)
            .toPromise();
        }
        this.load(this.dealId);
      });
  }
}
